import { ElementRef, Inject, Injectable } from '@angular/core';

import { map } from 'rxjs/operators';
import { Observable } from 'rxjs';

import { EL_DOCUMENT } from '../../../theme.options';
import {
  ElConnectedOverlayPositionChange,
  ElConnectedPosition,
  ElConnectionPositionPair,
  ElFlexibleConnectedPositionStrategy,
  ElOverlayPositionBuilder,
  ElOverlayRef,
  ElPlatform,
  ElPositionStrategy,
} from './mapping';
import { ElOverlayContainerAdapter } from '../adapter/overlay-container-adapter';
import { ElViewportRulerAdapter } from '../adapter/viewport-ruler-adapter';
import { ElGlobalLogicalPosition } from './position-helper';
import { GlobalPositionStrategy } from '@angular/cdk/overlay';


export enum ElAdjustment {
  NOOP = 'noop',
  CLOCKWISE = 'clockwise',
  COUNTERCLOCKWISE = 'counterclockwise',
  VERTICAL = 'vertical',
  HORIZONTAL = 'horizontal',
}

export enum ElPosition {
  TOP = 'top',
  BOTTOM = 'bottom',
  LEFT = 'left',
  RIGHT = 'right',
  START = 'start',
  END = 'end',
  TOP_END = 'top-end',
  TOP_START = 'top-start',
  BOTTOM_END = 'bottom-end',
  BOTTOM_START = 'bottom-start',
  END_TOP = 'end-top',
  END_BOTTOM = 'end-bottom',
  START_TOP = 'start-top',
  START_BOTTOM = 'start-bottom',
}

const POSITIONS = {
  [ElPosition.RIGHT](offset) {
    return { originX: 'end', originY: 'center', overlayX: 'start', overlayY: 'center', offsetX: offset };
  },
  [ElPosition.BOTTOM](offset) {
    return { originX: 'center', originY: 'bottom', overlayX: 'center', overlayY: 'top', offsetY: offset };
  },
  [ElPosition.LEFT](offset) {
    return { originX: 'start', originY: 'center', overlayX: 'end', overlayY: 'center', offsetX: -offset };
  },
  [ElPosition.TOP](offset) {
    return { originX: 'center', originY: 'top', overlayX: 'center', overlayY: 'bottom', offsetY: -offset };
  },
  [ElPosition.START](offset) {
    return this[ElPosition.LEFT](offset);
  },
  [ElPosition.END](offset) {
    return this[ElPosition.RIGHT](offset);
  },
  [ElPosition.END_TOP](offset) {
    return { originX: 'end', originY: 'bottom', overlayX: 'start', overlayY: 'bottom', offsetX: offset };
  },
  [ElPosition.END_BOTTOM](offset) {
    return { originX: 'end', originY: 'top', overlayX: 'start', overlayY: 'top', offsetX: offset };
  },
  [ElPosition.BOTTOM_START](offset) {
    return { originX: 'end', originY: 'bottom', overlayX: 'end', overlayY: 'top', offsetY: offset };
  },
  [ElPosition.BOTTOM_END](offset) {
    return { originX: 'start', originY: 'bottom', overlayX: 'start', overlayY: 'top', offsetY: offset };
  },
  [ElPosition.START_TOP](offset) {
    return { originX: 'start', originY: 'bottom', overlayX: 'end', overlayY: 'bottom', offsetX: -offset };
  },
  [ElPosition.START_BOTTOM](offset) {
    return { originX: 'start', originY: 'top', overlayX: 'end', overlayY: 'top', offsetX: -offset };
  },
  [ElPosition.TOP_START](offset) {
    return { originX: 'end', originY: 'top', overlayX: 'end', overlayY: 'bottom', offsetY: -offset };
  },
  [ElPosition.TOP_END](offset) {
    return { originX: 'start', originY: 'top', overlayX: 'start', overlayY: 'bottom', offsetY: -offset };
  },
};

const COUNTER_CLOCKWISE_POSITIONS = [
  ElPosition.TOP,
  ElPosition.TOP_END,
  ElPosition.TOP_START,
  ElPosition.START,
  ElPosition.START_TOP,
  ElPosition.START_BOTTOM,
  ElPosition.BOTTOM,
  ElPosition.BOTTOM_START,
  ElPosition.BOTTOM_END,
  ElPosition.END,
  ElPosition.END_BOTTOM,
  ElPosition.END_TOP,
];
const CLOCKWISE_POSITIONS = [
  ElPosition.TOP,
  ElPosition.TOP_START,
  ElPosition.TOP_END,
  ElPosition.END,
  ElPosition.END_TOP,
  ElPosition.END_BOTTOM,
  ElPosition.BOTTOM,
  ElPosition.BOTTOM_END,
  ElPosition.BOTTOM_START,
  ElPosition.START,
  ElPosition.START_BOTTOM,
  ElPosition.START_TOP,
];
const VERTICAL_POSITIONS = [ElPosition.BOTTOM, ElPosition.TOP];
const HORIZONTAL_POSITIONS = [ElPosition.START, ElPosition.END];


function comparePositions(p1: ElConnectedPosition, p2: ElConnectedPosition): boolean {
  return p1.originX === p2.originX
    && p1.originY === p2.originY
    && p1.overlayX === p2.overlayX
    && p1.overlayY === p2.overlayY;
}

/**
 * The main idea of the adjustable connected strategy is to provide predefined set of positions for your overlay.
 * You have to provide adjustment and appropriate strategy will be chosen in runtime.
 * */
export class ElAdjustableConnectedPositionStrategy
  extends ElFlexibleConnectedPositionStrategy implements ElPositionStrategy {

  protected _position: ElPosition;
  protected _offset: number = 15;
  protected _adjustment: ElAdjustment;

  protected appliedPositions: { key: ElPosition, connectedPosition: ElConnectedPosition }[];

  readonly positionChange: Observable<ElPosition> = this.positionChanges.pipe(
    map((positionChange: ElConnectedOverlayPositionChange) => positionChange.connectionPair),
    map((connectionPair: ElConnectionPositionPair) => {
      return this.appliedPositions.find(({ connectedPosition }) => {
        return comparePositions(connectedPosition, connectionPair);
      }).key;
    }),
  );

  attach(overlayRef: ElOverlayRef) {
    /**
     * We have to apply positions before attach because super.attach() validates positions and crashes app
     * if no positions provided.
     * */
    this.applyPositions();
    super.attach(overlayRef);
  }

  apply() {
    this.applyPositions();
    super.apply();
  }

  position(position: ElPosition): this {
    this._position = position;
    return this;
  }

  adjustment(adjustment: ElAdjustment): this {
    this._adjustment = adjustment;
    return this;
  }

  offset(offset: number): this {
    this._offset = offset;
    return this;
  }

  protected applyPositions() {
    const positions: ElPosition[] = this.createPositions();
    this.persistChosenPositions(positions);
    this.withPositions(this.appliedPositions.map(({ connectedPosition }) => connectedPosition));
  }

  protected createPositions(): ElPosition[] {
    switch (this._adjustment) {
      case ElAdjustment.NOOP:
        return [ this._position ];
      case ElAdjustment.CLOCKWISE:
        return this.reorderPreferredPositions(CLOCKWISE_POSITIONS);
      case ElAdjustment.COUNTERCLOCKWISE:
        return this.reorderPreferredPositions(COUNTER_CLOCKWISE_POSITIONS);
      case ElAdjustment.HORIZONTAL:
        return this.reorderPreferredPositions(HORIZONTAL_POSITIONS);
      case ElAdjustment.VERTICAL:
        return this.reorderPreferredPositions(VERTICAL_POSITIONS);
    }
  }

  protected persistChosenPositions(positions: ElPosition[]) {
    this.appliedPositions = positions.map(position => ({
      key: position,
      connectedPosition: POSITIONS[position](this._offset),
    }));
  }

  protected reorderPreferredPositions(positions: ElPosition[]): ElPosition[] {
    const startPositionIndex = positions.indexOf(this._position);
    const firstPart = positions.slice(startPositionIndex);
    const secondPart = positions.slice(0, startPositionIndex);
    return firstPart.concat(secondPart);
  }
}

export class ElGlobalPositionStrategy extends GlobalPositionStrategy {

  position(position: ElGlobalLogicalPosition): this {
    switch (position) {
      case ElGlobalLogicalPosition.TOP_START:
        return this.top().left();

      case ElGlobalLogicalPosition.TOP_END:
        return this.top().right();

      case ElGlobalLogicalPosition.BOTTOM_START:
        return this.bottom().left();

      case ElGlobalLogicalPosition.BOTTOM_END:
        return this.bottom().right();
    }
  }
}

@Injectable()
export class ElPositionBuilderService {
  constructor(@Inject(EL_DOCUMENT) protected document,
              protected viewportRuler: ElViewportRulerAdapter,
              protected platform: ElPlatform,
              protected positionBuilder: ElOverlayPositionBuilder,
              protected overlayContainer: ElOverlayContainerAdapter) {
  }

  global(): ElGlobalPositionStrategy {
    return new ElGlobalPositionStrategy();
  }

  connectedTo(elementRef: ElementRef): ElAdjustableConnectedPositionStrategy {
    return new ElAdjustableConnectedPositionStrategy(
      elementRef,
      this.viewportRuler,
      this.document,
      this.platform,
      this.overlayContainer,
    )
      .withFlexibleDimensions(false)
      .withPush(false);
  }
}
